<template>
  <v-dialog v-model="dialog" max-width="1280px" scrollable>
    <v-card class="shadowCard">
      <v-card-title class="py-4 px-5">
        <v-icon color="error" class="mr-3" large>mdi-ticket-confirmation-outline</v-icon>
        <div>
          <div class="text-h6 font-weight-bold mb-0">Tickets de alumnos sin respuesta</div>
          <div class="text-caption grey--text text--darken-1">
            Más de 24 horas sin respuesta del jefe o auxiliar
          </div>
        </div>
        <v-spacer></v-spacer>
        <v-chip small color="error" text-color="white" class="mr-2">
          {{ tickets.length }} alumnos
        </v-chip>
        <v-btn icon @click="dialog = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text class="px-5 py-4">
        <v-alert v-if="error" type="error" dense outlined>{{ error }}</v-alert>

        <v-row dense class="mb-2">
          <v-col cols="12" md="4">
            <v-select
              v-model="filtroMotivo"
              :items="motivos"
              label="Motivo"
              clearable
              filled
              dense
              hide-details
              prepend-inner-icon="mdi-filter-variant"
            />
          </v-col>
          <v-col cols="12" md="8">
            <v-text-field
              v-model="filtroTexto"
              label="Buscar"
              placeholder="Folio, alumno, matrícula, grupo..."
              prepend-inner-icon="mdi-magnify"
              clearable
              filled
              dense
              hide-details
            />
          </v-col>
        </v-row>

        <v-data-table
          :headers="headers"
          :items="ticketsFiltrados"
          :loading="cargando"
          loading-text="Cargando tickets..."
          no-data-text="No hay tickets de alumnos sin respuesta"
          class="elevation-0"
          dense
          fixed-header
          height="440"
          :items-per-page="15"
          :footer-props="{ 'items-per-page-options': [10, 15, 25, 50, -1] }"
        >
          <template v-slot:item.folio="{ item }">
            <span class="font-weight-bold">{{ item.folio }}</span>
          </template>

          <template v-slot:item.horas_sin_respuesta="{ item }">
            <v-chip x-small label :color="item.horas_sin_respuesta >= 48 ? 'error' : 'warning'" text-color="white">
              {{ tiempoSinRespuesta(item.horas_sin_respuesta) }}
            </v-chip>
          </template>

          <template v-slot:item.subio_ticket="{ item }">
            <span class="text-caption">{{ item.subio_ticket }}</span>
          </template>

          <template v-slot:item.alumno="{ item }">
            <div class="text-caption font-weight-medium">{{ item.alumno }}</div>
            <div class="text-caption grey--text">{{ item.matricula || '—' }}</div>
          </template>

          <template v-slot:item.grupo="{ item }">
            <span class="text-caption">{{ item.grupo }}</span>
          </template>

          <template v-slot:item.ciclo="{ item }">
            <span class="text-caption">{{ item.ciclo }}</span>
          </template>

          <template v-slot:item.motivo="{ item }">
            <v-chip x-small label outlined color="error">{{ item.motivo }}</v-chip>
          </template>

          <template v-slot:item.asignado_a="{ item }">
            <span class="text-caption">{{ item.asignado_a || '—' }}</span>
          </template>
        </v-data-table>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="px-5 py-3">
        <span class="text-caption grey--text">
          Mostrando {{ ticketsFiltrados.length }} de {{ tickets.length }}
        </span>
        <v-spacer></v-spacer>
        <v-btn text color="primary" :loading="cargando" @click="cargar">
          <v-icon left small>mdi-refresh</v-icon>
          Actualizar
        </v-btn>
        <v-btn color="primary" depressed @click="dialog = false">
          Cerrar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex'
import { esAlertaTicketsAutomatica } from '@/utils/alertaTicketsSinRespuesta'

const UNA_HORA = 60 * 60 * 1000
const REVISAR_CADA = 60 * 1000
const MAX_TICKETS_AVISO = 5

function escapar(valor) {
  return String(valor == null ? '' : valor)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

export default {
  name: 'AlertaTicketsSinRespuesta',

  data: () => ({
    dialog: false,
    cargando: false,
    error: '',
    tickets: [],
    filtroMotivo: null,
    filtroTexto: '',
    timer: null,
    headers: [
      { text: 'Folio', value: 'folio', width: '80px' },
      { text: 'Sin respuesta', value: 'horas_sin_respuesta', width: '120px' },
      { text: 'Subió el ticket', value: 'subio_ticket' },
      { text: 'Alumno', value: 'alumno' },
      { text: 'Grupo', value: 'grupo' },
      { text: 'Ciclo', value: 'ciclo', width: '130px' },
      { text: 'Motivo', value: 'motivo', width: '140px' },
      { text: 'Asignado a', value: 'asignado_a' },
    ],
  }),

  computed: {
    ...mapGetters('login', ['getdatosUsuarioERPERP']),

    automatica() {
      return esAlertaTicketsAutomatica(this.getdatosUsuarioERPERP)
    },

    claveUltimaAlerta() {
      const u = this.getdatosUsuarioERPERP
      return 'alertaTicketsSinRespuesta:' + (u ? u.iderp : '')
    },

    motivos() {
      return [...new Set(this.tickets.map((t) => t.motivo))].sort()
    },

    ticketsFiltrados() {
      let lista = this.tickets
      if (this.filtroMotivo) {
        lista = lista.filter((t) => t.motivo === this.filtroMotivo)
      }
      const q = String(this.filtroTexto || '').trim().toLowerCase()
      if (q) {
        lista = lista.filter((t) =>
          [t.folio, t.alumno, t.matricula, t.grupo, t.ciclo, t.subio_ticket, t.asignado_a, t.motivo]
            .some((v) => String(v || '').toLowerCase().includes(q))
        )
      }
      return lista
    },
  },

  watch: {
    automatica: {
      immediate: true,
      handler(activa) {
        this.detener()
        if (!activa) return
        this.revisarAutomatica()
        this.timer = setInterval(this.revisarAutomatica, REVISAR_CADA)
      },
    },
  },

  beforeDestroy() {
    this.detener()
  },

  methods: {
    detener() {
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
    },

    async cargar() {
      this.cargando = true
      this.error = ''
      try {
        const { data } = await this.$http.get('tickets.alerta.alumnos.sin.respuesta')
        this.tickets = (data && data.tickets) || []
      } catch (error) {
        this.error = (error.response && error.response.data && error.response.data.message) ||
          'No se pudieron cargar los tickets sin respuesta'
      } finally {
        this.cargando = false
      }
    },

    async abrir() {
      this.dialog = true
      await this.cargar()
    },

    async revisarAutomatica() {
      if (this.dialog || this.cargando) return
      const ultima = Number(localStorage.getItem(this.claveUltimaAlerta)) || 0
      if (Date.now() - ultima < UNA_HORA) return
      localStorage.setItem(this.claveUltimaAlerta, String(Date.now()))
      await this.cargar()
      if (this.tickets.length) await this.mostrarAviso()
    },

    async mostrarAviso() {
      const total = this.tickets.length
      const primero = this.tickets[0]
      const mensajePrincipal = total === 1
        ? `El ticket <b>${escapar(primero.folio)}</b> lleva <b>${escapar(this.tiempoSinRespuesta(primero.horas_sin_respuesta))}</b> sin respuesta del jefe o auxiliar, por el motivo: <b>${escapar(primero.motivo).toUpperCase()}</b>.`
        : `Hay <b>${total}</b> alumnos con tickets sin respuesta del jefe o auxiliar por más de 24 horas.`

      const detalleHtml = this.tickets.slice(0, MAX_TICKETS_AVISO).map((t) => `
        <div style="margin-top: 12px; background: #f3f4f6; border-radius: 8px; padding: 10px 12px; text-align: left; font-size: 13px; line-height: 1.45; color: #374151;">
          <div style="font-weight: 700; color: #111827;">
            ${escapar(t.plantel || 'Sin sucursal')}: ${escapar(t.alumno || 'Sin alumno')}
          </div>
          <div style="margin-top: 4px;">
            <span style="font-weight: 600;">Folio:</span> ${escapar(t.folio)}
            · <span style="font-weight: 600;">Motivo:</span> ${escapar(t.motivo)}
            · <span style="font-weight: 600;">Sin respuesta:</span> ${escapar(this.tiempoSinRespuesta(t.horas_sin_respuesta))}
          </div>
          <div><span style="font-weight: 600;">Subió:</span> ${escapar(t.subio_ticket)}</div>
          <div>
            <span style="font-weight: 600;">Grupo:</span> ${escapar(t.grupo)}
            · <span style="font-weight: 600;">Ciclo:</span> ${escapar(t.ciclo)}
          </div>
        </div>
      `).join('')

      const restantes = total - MAX_TICKETS_AVISO
      const restantesHtml = restantes > 0
        ? `<div style="margin-top: 12px; font-size: 13px; color: #6b7280;">Y ${restantes} más. Pulsa <b>Ver todos</b> para revisarlos.</div>`
        : ''

      const resultado = await this.$swal({
        title: 'Tickets de alumnos sin respuesta',
        html: `
          <div style="text-align: left; font-size: 15px; color: #374151;">
            ${mensajePrincipal}
            <div style="max-height: 340px; overflow-y: auto;">${detalleHtml}</div>
            ${restantesHtml}
          </div>
        `,
        icon: 'error',
        showConfirmButton: true,
        confirmButtonText: 'OK',
        showDenyButton: true,
        denyButtonText: 'Ver todos',
        denyButtonColor: '#6b7280',
      })

      if (resultado && resultado.isDenied) this.dialog = true
    },

    tiempoSinRespuesta(horas) {
      const h = Number(horas) || 0
      if (h < 48) return h + ' h'
      return Math.floor(h / 24) + ' días'
    },
  },
}
</script>
